import { useQuery, useLazyQuery } from "@apollo/client/react";
import { CircularProgress } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useLocation } from 'react-router-dom';
import { useVisita } from "../../../context/VisitaContext";
import GET_COMBOS_VISITAS, { adaptCombosVisitas } from "../../../data/graphQL/queries/MisVisitas/getCombosVisita";
import GET_FIGURA_DE_PROFESIONAL from "../../../data/graphQL/queries/MisVisitas/getFiguraDeProfesional";
import { useArchivosVisita } from "../hooks/useArchivosVisita";
import { useGetComplicacionesCombo } from '../hooks/useGetComplicacionesCombo';
import { useGetPantallasVisita } from "../hooks/useGetPantallasVisita";
import { useLoadVisita } from '../hooks/useLoadVisita';
import { useUltimoPasoIndex } from "../hooks/useUltimoPasoIndex";
import VisitaStepper from './components/VisitaStepper/VisitaStepper';
import { visitaSteps } from './components/steps/visitaSteps';
import { allowedTypesSaveInfoPaciente } from '../../MisVisitasCreation/constants/tiposProfesionales';
import { getUserCurrentLocation } from "../../../helpers/tools";

export const PACIENTE_EN_DOMICILIO_SI = 5;

export const ID_TIPO_VISITA_PRESENCIAL = 1;
export const ID_TIPO_VISITA_TELEENFERMERIA = 2;

const ID_FORMA_VISITA_PROGRAMADA = 3;
const ID_FORMA_VISITA_URGENCIA = 4;

const ID_INTERLOCUTOR_PACIENTE = 10;

export const MisVisitasCreationContent = () => {
  const location = useLocation();
  const {
    idPaciente,
    idEvaluacion,
    idFrecuencia,
    idProfesional,
    idTipoProfesional,
    idFiguraProfesional,
    figuraProfesional,
    idTipoModulo,
    idVisita,
    isNew,
    onlyView,
    programa,
    idTipoVisita,
    idFuncionProfesional,
    visitasProgramadas,
    periodo,
    esVisitaCero,
    isClone,
    desdeCoordinacion,
    periodoCoordinador,
    nroDocumentoPaciente
  } = location.state;
  const { visita, setVisita } = useVisita();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [exposedFormattedByStep, setExposedFormattedByStep] = useState({});
  const [combosVisita, setCombosVisita] = useState([]);

  const esEnfermeroGuardia = idTipoProfesional == 7;
  const esConsultor = ["CONSULTOR"].includes(figuraProfesional?.trim()?.toUpperCase());
  const esUrgencia = useMemo(() => visita?.idComboVisitaValorFormaVisita === ID_FORMA_VISITA_URGENCIA, [visita?.idComboVisitaValorFormaVisita]);
  const [esEnfermeroCuidador, setEsEnfermeroCuidador] = useState(false);

  //Geolocalización
  const [coordenadas, setCoordenadas] = useState({ posY: null, posX: null });
  const [geoLoading, setGeoLoading] = useState(false);

  const { data: dataCombosVisitas } = useQuery(GET_COMBOS_VISITAS, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataCombosVisitas) return;
    const adaptedCombos = adaptCombosVisitas(dataCombosVisitas);
    setCombosVisita(adaptedCombos);
  }, [dataCombosVisitas]);

  useEffect(() => {
    if (esVisitaCero) {
      setVisita(prev => ({
        ...prev,
        idComboVisitaValorTipoVisita: ID_TIPO_VISITA_TELEENFERMERIA,
        idComboVisitaValorFormaVisita: ID_FORMA_VISITA_PROGRAMADA,
        codigoAtencion: '0',
        idComboVisitaValorInterlocutor: ID_INTERLOCUTOR_PACIENTE,
        nombreCompleto: visita?.paciente,
        dNI: nroDocumentoPaciente,
        esVisitaCero: esVisitaCero
      }));
    }
  }, [esVisitaCero, visita?.paciente, nroDocumentoPaciente]);

  useEffect(() => {
    setVisita(prev => ({
      ...prev,
      desdeCoordinacion: !!desdeCoordinacion
    }));
  }, [desdeCoordinacion]);

  const { currentStackScreens, setCurrentStackScreens, refetchPantallas, loadingPantallas } = useGetPantallasVisita({ idTipoProfesional, idTipoModulo, idProfesional, idEvaluacion, idFiguraProfesional });

  const complicacionesCombo = useGetComplicacionesCombo(idTipoModulo);

  const { ultimoPasoIndex, updateUltimoPasoIndex } = useUltimoPasoIndex(visita?.idVisita, currentStackScreens);

  const saveStepRef = useRef();

  const handleSave = async () => {
    const currentStep = currentStackScreens[currentStepIndex];

    if (currentStep.readOnly || onlyView) {
      return;
    }

    if (saveStepRef.current) {
      try {
        await toast.promise(
          saveStepRef.current(false),
          {
            loading: 'Guardando...',
            success: 'Guardado correctamente',
            error: (err) => err.isCustomError ? '' : 'Error al guardar'
          }
        );
      } catch (error) {
        console.error('handleSave Catch:', error);
        // ya lo maneja el toast
      }
    }
  };

  const handleNext = async () => {
    const currentStep = currentStackScreens[currentStepIndex];

    if (currentStep.readOnly || onlyView) {
      setCurrentStepIndex((prev) => prev + 1);
      return;
    }

    if (saveStepRef.current) {
      try {
        await toast.promise(
          saveStepRef.current(true),
          {
            loading: 'Guardando...',
            success: 'Guardado correctamente',
            error: (err) => err.isValidationError || err.isCustomError ? '' : 'Error al guardar'
          }
        );
        if (currentStepIndex < currentStackScreens.length - 1) {
          const nextStepIndex = currentStepIndex + 1;
          setCurrentStepIndex(nextStepIndex);
          if (nextStepIndex > ultimoPasoIndex && !allowedTypesSaveInfoPaciente.includes(idTipoProfesional)) {
            await updateUltimoPasoIndex(nextStepIndex);
          }
          if (allowedTypesSaveInfoPaciente.includes(idTipoProfesional)) {
            await updateUltimoPasoIndex(currentStackScreens.length - 1);
          }
        }
      } catch (error) {
        console.error('handleNext Catch:', error);
        // No avanzar si falló
      }
    }
  };

  const handleExposeFormatted = useCallback(({ stepId, stepName, formattedInputs = [], formattedGroups = [] }) => {
    setExposedFormattedByStep((prev) => ({
      ...prev,
      [stepId]: { stepName, formattedInputs, formattedGroups }
    }));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStepIndex]);

  useEffect(() => {
    setVisita((prev) => ({
      ...prev,
      idVisita: prev.idVisita ?? idVisita,
      idEvaluacion,
      idFrecuencia,
      idProfesional,
      idTipoProfesional,
      idFiguraProfesional,
      idComboVisitaValorTipoVisita: prev.idComboVisitaValorTipoVisita ?? idTipoVisita,
      idPaciente,
      programa
    }));
  }, [idVisita, idEvaluacion, idFrecuencia, idProfesional, idTipoProfesional, idFiguraProfesional, idTipoVisita, idPaciente]);

  useEffect(() => {
    setGeoLoading(true);
    getUserCurrentLocation().
      then((coords) => {
        const nuevasCoords = {
          posY: coords.lat?.toString() ?? null,
          posX: coords.lng?.toString() ?? null
        };
        setCoordenadas(nuevasCoords);
        setVisita((prev) => ({ ...prev, ...nuevasCoords }));
      }).
      catch((err) => {
        console.error("Error al obtener ubicación:", err.message);
        toast.error('Ocurrió un error al obtener la ubicación.');
      })
      .finally(() => {
        console.log('getUserCurrentLocation');
        setGeoLoading(false);
      });
  }, []);

  const { loading: loadingVisita, error: errorVisita, reload } = useLoadVisita({
    isNew,
    tipoVisita: idTipoVisita,
    programa,
    idVisita,
    setVisita,
    toastOnLoad: true,
    idTipoProfesional,
    idFiguraProfesional,
    idEvaluacion
  });

  useArchivosVisita(visita.idVisita);

  const sharedProps = {
    visita,
    setVisita,
    idPaciente,
    idEvaluacion,
    idProfesional,
    idFrecuencia,
    onGuardar: handleNext,
    exposedFormattedByStep,
    programa,
    isClone,
    desdeCoordinacion: !!desdeCoordinacion,
    periodoCoordinador,
    idTipoProfesional,
    idFiguraProfesional,
    figuraProfesional,
    complicacionesCombo,
    visitasProgramadas,
    periodo,
    esEnfermeroGuardia,
    esVisitaCero,
    esEnfermeroCuidador,
    setEsEnfermeroCuidador,
    currentStackScreens,
    setCurrentStackScreens,
    refetchPantallas,
    coordenadas
  };

  if (loadingPantallas || geoLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '80vh'
      }}>
        <CircularProgress />
      </div>);

  }

  return (
    <>
      <VisitaStepper
        steps={currentStackScreens}
        currentIndex={currentStepIndex}
        onStepChange={setCurrentStepIndex}
        onSave={handleSave}
        onNext={handleNext}
        ultimoPasoIndex={ultimoPasoIndex}
        onlyView={onlyView}
        esConsultor={esConsultor}
      />
      {currentStackScreens && currentStackScreens.map(({ component: CurrentScreenComp, screenReadOnly }, index) =>
        <div
          key={index}
          style={currentStepIndex === index ? { position: 'inherit', marginTop: "1rem" } : { visibility: 'hidden', position: 'fixed' }}>

          <CurrentScreenComp
            {...sharedProps}
            esUrgencia={esUrgencia}
            figuraProfesional={figuraProfesional}
            combosVisita={combosVisita}
            readOnly={screenReadOnly || onlyView}
            onSaveRef={index === currentStepIndex ? saveStepRef : undefined}
            isActiveStep={index === currentStepIndex}
            onExposeFormatted={handleExposeFormatted} />

        </div>
      )}
    </>);

};